import { aeEmirates, auStates, caProvinces, countries, usStates } from 'boot/countries';
import { filterPropertyTypes } from 'boot/globals';
import { i18n } from 'boot/i18n';
import { Timestamp } from 'firebase/firestore';
import { chain, concat, find, get, head, isArray, isEmpty, join, map } from 'lodash';
import { CLIENT_DATA_COLUMN_GROUP } from 'src/constants/clients';
import { auth as useAuthStore } from 'src/store/auth';
import { clients as useClientsStore } from 'src/store/clients';
import { dataFields as useDataFieldsStore } from 'src/store/dataFields';
import { units as useUnitsStore } from 'src/store/units';
import { calculateAge } from 'src/utils/DateUtils';
import { computed } from 'vue';
import { useTime } from 'src/composables/time';

// eslint-disable-next-line import/prefer-default-export
export function useClientColumns({ personType = 'clients' } = {}) {
  const authStore = useAuthStore();
  const clientsStore = useClientsStore();
  const dataFieldsStore = useDataFieldsStore();
  const unitsStore = useUnitsStore();
  const { getLocaleDateString, getLocaleTimeString } = useTime();

  const availableIntegrations = [
    {
      id: 'aloha',
      label: 'AlohaABA',
      authMethod: 'externalId',
      logo: 'integrations/logo-aloha.png',
      supports: {},
    },
    {
      id: 'amrex',
      label: 'TherapyPMS',
      authMethod: 'emailPwd',
      logo: 'integrations/logo-therapypms.png',
      supports: {
        manualReloadOfAllAppointments: true,
      },
    },
    {
      id: 'lumary',
      label: 'Lumary',
      authMethod: 'apiPath',
      logo: 'integrations/logo-lumary.png',
      supports: {
        manualReloadOfAllAppointments: true,
      },
    },
    {
      id: 'vireo',
      label: 'Vireo',
      authMethod: 'url',
      logo: 'integrations/logo-vireo.gif',
      supports: {
        manualReloadOfAllAppointments: true,
      },
    },
    {
      id: 'boost',
      label: 'Boost - Closed BETA',
      authMethod: '',
      logo: 'integrations/logo-boost.png',
      hide: true,
      supports: {},
    },
    {
      id: 'camber',
      label: 'Camber - Closed BETA',
      authMethod: '',
      logo: 'integrations/logo-camber.png',
      hide: true,
      supports: {},
    },
  ];

  function normalizeCountryLabel(label) {
    return isArray(label) ? label.join(', ') : label;
  } // normalizeCountryLabel()

  function countryStates(countryCode) {
    switch (countryCode) {
      case 'US': {
        return usStates;
      }
      case 'AU': {
        return auStates;
      }
      case 'AE': {
        return aeEmirates;
      }
      case 'CA': {
        return caProvinces;
      }
      default:
    }
    return [];
  } // countryStates()

  function getIntegrationName(id) {
    return chain(availableIntegrations)
      .find({ id })
      .get('label')
      .defaultTo('Hi Rasmus')
      .value();
  } // getIntegrationName()

  function formatCustomDataField(clientDataField, customData) {
    const value = get(customData, clientDataField.id, null);
    switch (clientDataField.type) {
      case 'date': {
        if (value instanceof Timestamp) {
          return getLocaleDateString(value);
        }
        return '';
      }
      case 'select': {
        if (isArray(value)) {
          return join(value, ', ');
        }
        return value;
      }
      case 'yesNo': {
        if (typeof value === 'boolean') {
          return value ? i18n.t('common.buttonYes') : i18n.t('common.buttonNo');
        }
        return i18n.t('common.notSelected');
      }
      case 'user': {
        return get(value, 'userName', '');
      }
      default: {
        return value;
      }
    }
  } // formatCustomDataField()

  function getCustomDataFieldFilterType(clientDataField) {
    switch (clientDataField.type) {
      case 'date':
        return filterPropertyTypes.Date;
      case 'select':
        return clientDataField.allowMultipleOptions
          ? filterPropertyTypes.ArrayInArray
          : filterPropertyTypes.StringInArray;
      case 'yesNo':
        return filterPropertyTypes.Boolean;
      case 'user':
        return filterPropertyTypes.User;
      default:
        return filterPropertyTypes.StringMatch;
    }
  } // getCustomDataFieldFilterType()

  function getCustomDataFieldFilterProperty(clientDataField) {
    if (clientDataField.type === 'user') {
      return `customData.${clientDataField.id}.user`;
    }
    return `customData.${clientDataField.id}`;
  } // getCustomDataFieldFilterProperty()

  function getCustomDataFieldFilterItems(customDataField) {
    switch (customDataField.type) {
      case 'select':
        return {
          filterItems: map(customDataField.options, (filterItem) => ({
            value: filterItem,
            label: filterItem,
          })),
        };
      default:
        return {};
    }
  } // getCustomDataFieldFilterItems()

  const careModelCohortsFilterItems = computed(() => {
    const retVal = map(
      authStore.careModelCohorts || [],
      (cohort) => ({
        value: cohort.id,
        label: cohort.cohortName,
      }),
    );

    return concat(retVal, {
      value: null,
      label: i18n.t('cohorts.unassignedLabel'),
    });
  });

  const businessUnitsFilterItems = computed(() => {
    const retVal = map(
      unitsStore.userUnits || [],
      (unit) => ({
        value: unit.id,
        label: unit.unitName,
      }),
    );

    if (authStore.isManagerOrAdministratorOrCoordinator) {
      return concat(retVal, {
        value: null,
        label: i18n.t('settings.units.noUnitsLabel'),
      });
    }

    return retVal;
  });

  const countryFilterItems = computed(() => {
    const filterItemsList = chain(clientsStore.clients || [])
      .map((client) => client.country?.toUpperCase())
      .uniq()
      .value();

    const alpha3Codes = countries.getAlpha3Codes();
    return chain(alpha3Codes)
      .pickBy((value) => filterItemsList.includes(value))
      .map((code) => ({
        label: normalizeCountryLabel(i18n.t(code)),
        value: code,
        flagIcon: code,
      }))
      .sortBy('label')
      .value();
  });

  const stateFilterItems = computed(() => {
    return chain(clientsStore.clients || [])
      .filter((client) => client.state)
      .map((client) => {
        const stateLabel = chain(countryStates(client.country))
          .find({ code: client.state })
          .get('label')
          .value();

        if (!stateLabel) {
          return null;
        }

        return {
          value: client.state,
          label: stateLabel,
        };
      })
      .compact()
      .uniqBy('value')
      .sortBy('label')
      .value();
  });

  const integrationColumns = computed(() => {
    if (!get(authStore.connectedClinic, 'integration')) {
      return [];
    }

    const integrationName = getIntegrationName(authStore.connectedClinic.integration);

    return [
      {
        name: 'integration',
        group: 'integration',
        align: 'left',
        label: i18n.t(
          'settings.users.columnsIntegrationLabel',
          { integration: integrationName },
        ),
        field: 'integrationReference',
        sortable: true,
        profileTypes: ['client'],

        filterable: true,
        filterType: filterPropertyTypes.NullValue,
        filterItems: [
          {
            value: false,
            label: i18n.t('settings.users.labelIntegrationConnected'),
          },
          {
            value: true,
            label: i18n.t('settings.users.labelIntegrationNotConnected'),
          },
        ],
      },
      {
        name: 'integrationId',
        group: 'integration',
        align: 'left',
        label: i18n.t(
          'settings.users.columnsIntegrationIdLabel',
          { integration: integrationName },
        ),
        field: 'integrationReference',
        sortable: true,
        profileTypes: ['client'],

        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
      },
    ];
  });

  const lastSessionColumns = computed(() => {
    return [
      {
        name: 'lastSessionDate',
        group: 'lastSession',
        label: i18n.t('clients.columnsLastSessionDateLabel'),
        align: 'left',
        field: (row) => get(row, 'lastSession.runTimestamp'),
        sortable: true,
        format: (runTimestamp) => (runTimestamp instanceof Timestamp ? getLocaleTimeString(runTimestamp, {
          options: { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' },
        }) : ''),
        profileTypes: ['client'],
        filterable: true,
        filterType: filterPropertyTypes.Date,
        filterProperty: 'lastSession.runTimestamp',
        filterDateOperators: true,
      },
      {
        name: 'lastSessionName',
        group: 'lastSession',
        label: i18n.t('clients.columnsLastSessionNameLabel'),
        align: 'left',
        field: (row) => get(row, 'lastSession.sessionName'),
        sortable: true,
        format: (sessionName) => sessionName || '',
        profileTypes: ['client'],
        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
        filterProperty: 'lastSession.sessionName',
      },
    ];
  });

  const clientColumns = computed(() => {
    return [
      {
        name: 'clientName',
        required: true,
        label: i18n.t('clients.columnsClientNameLabel'),
        align: 'left',
        field: 'clientName',
        type: 'text',
        sortable: true,
        mandatory: true,
        profileTypes: ['client'],
      },
      {
        name: 'clientName',
        label: i18n.t('clients.columnsStaffMemberNameLabel'),
        align: 'left',
        field: 'clientName',
        type: 'text',
        sortable: true,
        profileTypes: ['staffMember'],
      },
      {
        name: 'firstName',
        required: true,
        label: i18n.t('clients.columnsFirstNameLabel'),
        align: 'left',
        field: 'firstName',
        type: 'text',
        sortable: true,
        profileTypes: ['client', 'staffMember'],
      },
      {
        name: 'lastName',
        required: true,
        label: i18n.t('clients.columnsLastNameLabel'),
        align: 'left',
        field: 'lastName',
        type: 'text',
        sortable: true,
        profileTypes: ['client', 'staffMember'],
      },
      {
        name: 'alias',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientAliasLabel'),
        align: 'left',
        field: 'alias',
        type: 'text',
        sortable: true,
        profileTypes: ['client'],

        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
        filterProperty: 'alias',
      },
      {
        name: 'reference',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientReferenceIDLabel'),
        align: 'left',
        field: 'reference',
        type: 'text',
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
        filterProperty: 'reference',
      },
      {
        name: 'gender',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientGenderLabel'),
        align: 'left',
        field: 'gender',
        type: 'select',
        options: map(i18n.tm('clients.modals.editClient.gender'), (genderLabel, genderKey) => ({
          value: genderKey,
          label: genderLabel,
        })),
        format: (gender) => get(i18n.tm('clients.modals.editClient.gender'), gender, ''),
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.StringInArray,
        filterProperty: 'gender',
        filterItems: map(i18n.tm('clients.modals.editClient.gender'), (genderLabel, genderKey) => ({
          value: genderKey,
          label: genderLabel,
        })),
      },
      {
        name: 'birthDate',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientDOBLabel'),
        align: 'left',
        field: 'birthDate',
        type: 'date',
        format: (birthDate) => ((birthDate instanceof Timestamp) ? getLocaleDateString(birthDate) : null),
        sortable: true,
        profileTypes: ['client'],
      },
      {
        name: 'age',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientAgeLabel'),
        align: 'left',
        field: 'birthDate',
        format: (birthDate) => calculateAge(birthDate),
        sortable: true,
        profileTypes: ['client'],

        filterable: true,
        filterType: filterPropertyTypes.Number,
        filterProperty: 'birthDate',
        filterPropertyCallback: (row) => calculateAge(row.birthDate, false),
      },
      {
        name: 'email',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientEmailLabel'),
        align: 'left',
        field: 'email',
        type: 'text',
        sortable: true,
        profileTypes: ['client'],

        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
        filterProperty: 'email',
      },
      {
        name: 'phone',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientPhoneLabel'),
        align: 'left',
        field: 'phone',
        type: 'text',
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
        filterProperty: 'phone',
      },
      {
        name: 'address1',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientAddress1Label'),
        align: 'left',
        field: 'address1',
        type: 'text',
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
        filterProperty: 'address1',
      },
      {
        name: 'address2',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientAddress2Label'),
        align: 'left',
        field: 'address2',
        type: 'text',
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
        filterProperty: 'address2',
      },
      {
        name: 'postalCode',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientPostalCodeLabel'),
        align: 'left',
        field: 'postalCode',
        type: null,
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
        filterProperty: 'postalCode',
      },
      {
        name: 'city',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientCityLabel'),
        align: 'left',
        field: 'city',
        type: null,
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
        filterProperty: 'city',
      },
      {
        name: 'state',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientStateLabel'),
        align: 'left',
        field: 'state',
        type: null,
        format: (state, client) => chain(countryStates(get(client, 'country', null)))
          .find({ code: state })
          .get('label', '')
          .defaultTo(state || '')
          .value(),
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.StringInArray,
        filterItems: stateFilterItems.value,
      },
      {
        name: 'country',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientCountryLabel'),
        align: 'left',
        field: 'country',
        type: null,
        format: (country) => (country ? i18n.t(country) : ''),
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.StringInArray,
        filterItems: countryFilterItems.value,
      },
      {
        name: 'units',
        align: 'left',
        label: i18n.t('settings.units.columnUnitLabel'),
        field: 'units',
        type: null,
        format: (val) => {
          if (isEmpty(val)) {
            return '';
          }

          const unit = find(unitsStore.units || [], { id: head(val) });

          return get(unit, 'unitName', '');
        },
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.ArrayInArray,
        filterItems: businessUnitsFilterItems.value,
        // Stored as an array to leave room for more, but a client is assigned exactly one unit.
        filterSingleValued: true,
      },
      ...(authStore.careModelCohorts?.length > 0
        ? [{
          name: 'careModelCohort',
          align: 'left',
          label: i18n.t('clients.modals.editClient.inputClientCohortLabel'),
          field: 'careModelCohort',
          type: null,
          format: (cohortId) => {
            if (!cohortId) {
              return i18n.t('cohorts.unassignedLabel');
            }

            const cohort = find(authStore.careModelCohorts, { id: cohortId });

            return get(cohort, 'cohortName', '');
          },
          sortable: true,
          profileTypes: ['client'],

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterItems: careModelCohortsFilterItems.value,
        }]
        : []),
      {
        name: 'employeeStartDate',
        align: 'left',
        label: i18n.t('staff.modals.editStaffMember.inputClientEmployeeStartDateLabel'),
        field: 'employeeStartDate',
        type: 'date',
        format: (data) => ((data instanceof Timestamp) ? getLocaleDateString(data) : null),
        sortable: true,
        profileTypes: ['staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.Date,
        filterProperty: 'employeeStartDate',
      },
      {
        name: 'employeeEndDate',
        align: 'left',
        label: i18n.t('staff.modals.editStaffMember.inputClientEmployeeEndDateLabel'),
        field: 'employeeEndDate',
        type: 'date',
        format: (data) => ((data instanceof Timestamp) ? getLocaleDateString(data) : null),
        sortable: true,
        profileTypes: ['staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.Date,
        filterProperty: 'employeeEndDate',
      },
      {
        name: 'status',
        align: 'left',
        label: personType === 'clients'
          ? i18n.t('clients.columnsStatusLabel')
          : i18n.t('staff.modals.editStaffMember.columnsLicenseTypeLabel'),
        format: (status) => get(
          i18n.tm(personType === 'clients' ? 'clients.status' : 'staff.status'),
          status,
          '?',
        ),
        field: 'status',
        sortable: true,
        profileTypes: ['client', 'staffMember'],

        filterable: true,
        filterType: filterPropertyTypes.StringInArray,
        filterProperty: 'status',
        filterItems: map(
          i18n.tm(personType === 'clients' ? 'clients.status' : 'staff.status'),
          (statusLabel, statusKey) => ({
            value: statusKey,
            label: statusLabel,
          }),
        ),
      },
      {
        name: 'id',
        required: true,
        label: i18n.t('clients.modals.editClient.inputClientIdLabel'),
        align: 'left',
        field: 'id',
        sortable: true,
        profileTypes: ['client'],

        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
        filterProperty: 'id',
      },
    ];
  });

  const clientDataColumns = computed(() => {
    return chain(dataFieldsStore.clientDataFields || [])
      .sortBy('order')
      .map((clientDataField) => ({
        name: clientDataField.id,
        label: clientDataField.label,
        align: 'left',
        field: 'customData',
        format: (customData) => formatCustomDataField(clientDataField, customData),
        sortable: false,
        mandatory: false,
        profileTypes: ['client'],

        filterable: true,
        filterType: getCustomDataFieldFilterType(clientDataField),
        filterProperty: getCustomDataFieldFilterProperty(clientDataField),
        ...getCustomDataFieldFilterItems(clientDataField),
      }))
      .value();
  });

  const staffMemberDataColumns = computed(() => {
    return map(dataFieldsStore.allStaffMemberDataFields || [], (staffMemberDataField) => ({
      name: staffMemberDataField.id,
      label: staffMemberDataField.label,
      align: 'left',
      field: 'customData',
      format: (customData) => formatCustomDataField(staffMemberDataField, customData),
      sortable: false,
      mandatory: false,
      profileTypes: ['staffMember'],

      filterable: true,
      filterType: getCustomDataFieldFilterType(staffMemberDataField),
      filterProperty: getCustomDataFieldFilterProperty(staffMemberDataField),
      ...getCustomDataFieldFilterItems(staffMemberDataField),
    }));
  });

  function buildColumns({
    includeClientData = true,
    includeIntegration = false,
    includeLastSession = false,
  } = {}) {
    const columns = chain(clientColumns.value)
      .filter((column) => column.profileTypes?.includes('client'))
      .value();

    if (includeClientData) {
      columns.push(
        ...map(clientDataColumns.value, (clientDataColumn) => ({
          ...clientDataColumn,
          group: CLIENT_DATA_COLUMN_GROUP,
        })),
      );
    }

    if (includeIntegration) {
      columns.push(...integrationColumns.value);
    }

    if (includeLastSession) {
      columns.push(...lastSessionColumns.value);
    }

    return columns;
  } // buildColumns()

  function buildColumnGroups({
    includeClientData = true,
    includeIntegration = false,
    includeLastSession = false,
  } = {}) {
    const groups = [];

    if (includeClientData) {
      groups.push({
        group: CLIENT_DATA_COLUMN_GROUP,
        label: i18n.t('clients.columnGroupClientData'),
      });
    }

    if (includeIntegration && integrationColumns.value.length > 0) {
      groups.push({
        group: 'integration',
        label: i18n.t('clients.columnGroupIntegration', {
          integration: getIntegrationName(get(authStore.connectedClinic, 'integration')),
        }),
      });
    }

    if (includeLastSession) {
      groups.push({
        group: 'lastSession',
        label: i18n.t('clients.columnGroupLastSession'),
      });
    }

    return groups;
  } // buildColumnGroups()

  return {
    clientColumns,
    clientDataColumns,
    staffMemberDataColumns,
    integrationColumns,
    lastSessionColumns,
    buildColumns,
    buildColumnGroups,
    getIntegrationName,
  };
}
